import styled from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";
import { BsPlus, BsPeople, BsSearch } from "react-icons/bs";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { ITeam } from "MVVM/@types";
import { useSubscriptionInfo } from "MVVM/Hooks/useHasProAccess";
import { TeamCard } from "../../Components/Teams/TeamCard";
import { usePortal } from "MVVM/Hooks/usePortal";
import { UnifiedUpgradeModal } from "MVVM/Components/Modals/UnifiedUpgradeModal";
import { CreateTeamMenu } from "./Components/CreateTeamMenu";
import { TeamsHubViewModel, useDiscoverTeams } from "./ViewModel";
import { DiscoverCard } from "./Components/DiscoverCard";
import { GameFilter } from "./Components/GameFilter";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import { useGetUser } from "userStore";

type TTab = "my" | "find";
type TSort = "POPULAR" | "NEW";

const TeamsHub = () => {
  const currentUser = useGetUser();
  const history = useHistory();
  const [tab, setTab] = useState<TTab>("find");
  const [gameFilter, setGameFilter] = useState<string | null>(null);
  const [sort, setSort] = useState<TSort>("POPULAR");

  const { getViewerTeams, refetchTeams, teams, isLoadingTeams } =
    TeamsHubViewModel();
  const { discoverTeams, isLoadingDiscover } = useDiscoverTeams(
    gameFilter ?? undefined,
    sort,
  );
  const { isEssentials, isProOrHigher } = useSubscriptionInfo();

  useEffect(() => {
    if (currentUser) {
      getViewerTeams();
    }
  }, [currentUser, getViewerTeams]);

  const handleTeamCreated = async () => {
    if (refetchTeams) {
      await refetchTeams();
    }
  };

  const {
    portalOpen: subscriptionPaywallOpen,
    setPortalOpen: setSubscriptionPaywallOpen,
    Portal: SubscriptionPaywallPortal,
  } = usePortal(
    <UnifiedUpgradeModal
      featureName="Team Creation"
      upgradeAction="Create a Team"
      requiredTierKey="ESSENTIALS"
      analyticsSource="Teams Hub"
      onClose={() => setSubscriptionPaywallOpen(false)}
    />,
  );

  const handleCreateTeamClick = () => {
    if (!currentUser) {
      history.push("/login?next=" + encodeURIComponent("/teams"));
      return;
    }
    const canCreate = isEssentials || isProOrHigher;
    if (!canCreate) {
      setSubscriptionPaywallOpen(true);
      return;
    }
    setCreateTeamModalOpen(true);
  };

  const {
    portalOpen: createTeamModalOpen,
    setPortalOpen: setCreateTeamModalOpen,
    Portal: CreateTeamPortal,
  } = usePortal(
    <CreateTeamMenu
      onClose={() => setCreateTeamModalOpen(false)}
      onCreateTeam={handleTeamCreated}
    />,
  );

  return (
    <Container>
      <BannerBg />
      <Content>
        <PageHead>
          <PageHeadRow>
            <div>
              <PageTitle>
                {tab === "my" ? (
                  <>
                    Your <Accent>Teams</Accent>
                  </>
                ) : (
                  <>
                    Discover <Accent>Teams</Accent>
                  </>
                )}
              </PageTitle>
              <PageSub>
                {tab === "my"
                  ? "Drop into team feeds, run montage night, or scout new crews."
                  : "Scout fresh crews. Filter by game or browse what's popular."}
              </PageSub>
            </div>
            <CreateButton onClick={handleCreateTeamClick}>
              <BsPlus size={18} />
              <CreateButtonLabel>Create a Team</CreateButtonLabel>
            </CreateButton>
          </PageHeadRow>

          <Tabs>
            <Tab
              $active={tab === "my"}
              onClick={() => {
                if (!currentUser) {
                  history.push("/login?next=" + encodeURIComponent("/teams"));
                  return;
                }
                setTab("my");
              }}
            >
              <BsPeople size={15} />
              My Teams
              {currentUser && teams.length > 0 && (
                <TabCount>{teams.length}</TabCount>
              )}
            </Tab>
            <Tab $active={tab === "find"} onClick={() => setTab("find")}>
              <BsSearch size={14} />
              Find Teams
              <NewBadge>NEW</NewBadge>
            </Tab>
          </Tabs>
        </PageHead>

        {tab === "find" && (
          <FilterRow>
            <FilterLeft>
              <GameFilter active={gameFilter} onChange={setGameFilter} />
            </FilterLeft>
            <SortTabs>
              <SortTab
                $active={sort === "POPULAR"}
                onClick={() => setSort("POPULAR")}
              >
                Popular
              </SortTab>
              <SortTab $active={sort === "NEW"} onClick={() => setSort("NEW")}>
                New
              </SortTab>
            </SortTabs>
          </FilterRow>
        )}

        <Page>
          {tab === "my" && currentUser && (
            <>
              {isLoadingTeams ? (
                <LoadingContainer>
                  <LoadingSpinner width={48} height={48} />
                </LoadingContainer>
              ) : teams.length > 0 ? (
                <MyTeamsGrid>
                  {teams.map((team: ITeam) => (
                    <TeamCard key={team.id} team={team} />
                  ))}
                  {teams.length < 3 && (
                    <CreateCard onClick={handleCreateTeamClick}>
                      <CreateCardInner>
                        <CreateCircle>
                          <BsPlus size={22} />
                        </CreateCircle>
                        <Text fontWeight={600} fontSize={15}>
                          Start a new Team
                        </Text>
                        <Text
                          color="chalk"
                          fontSize={13}
                          style={{ textAlign: "center" }}
                        >
                          Bring your squad together. Share clips, compete as
                          one.
                        </Text>
                      </CreateCardInner>
                    </CreateCard>
                  )}
                </MyTeamsGrid>
              ) : (
                <EmptyState>
                  <EmptyTitle>No Teams Yet</EmptyTitle>
                  <EmptyDesc>Create or join a team to get started</EmptyDesc>
                  <CreateButton onClick={handleCreateTeamClick}>
                    <BsPlus size={16} />
                    Create a Team
                  </CreateButton>
                </EmptyState>
              )}
            </>
          )}

          {tab === "find" && (
            <>
              {isLoadingDiscover ? (
                <LoadingContainer>
                  <LoadingSpinner width={48} height={48} />
                </LoadingContainer>
              ) : discoverTeams.length > 0 ? (
                <DiscoverGrid>
                  {discoverTeams.map((team: ITeam) => (
                    <DiscoverCard key={team.id} team={team} />
                  ))}
                </DiscoverGrid>
              ) : (
                <EmptyDiscover>
                  No teams found for this game yet.{" "}
                  <CreateLink onClick={handleCreateTeamClick}>
                    Be the first to start one →
                  </CreateLink>
                </EmptyDiscover>
              )}
            </>
          )}
        </Page>
      </Content>

      {createTeamModalOpen && CreateTeamPortal}
      {subscriptionPaywallOpen && SubscriptionPaywallPortal}
    </Container>
  );
};

const Container = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.ned};
  flex-direction: column;
  min-height: 100vh;
  position: relative;
  width: 100%;
`;

const BannerBg = styled(Box)`
  height: 340px;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 0;

  &::before {
    background-image: url(${process.env
      .REACT_APP_S3_CLIP_BUCKET}/static/web/teams/hub/teams-background.jpg);
    background-position: 50% 40%;
    background-repeat: no-repeat;
    background-size: cover;
    bottom: 0;
    content: "";
    left: 0;
    opacity: 0.35;
    position: absolute;
    right: 0;
    top: 0;
  }

  &::after {
    background: linear-gradient(
      to bottom,
      transparent 20%,
      ${({ theme }) => theme.colors.ned}
    );
    bottom: 0;
    content: "";
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

const Content = styled(Flex)`
  flex-direction: column;
  margin: 0 auto;
  max-width: 1240px;
  padding: 100px 60px 60px;
  position: relative;
  width: 100%;
  z-index: 1;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    padding: 80px 24px 40px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    padding: 60px 16px 40px;
  }
`;

const PageHead = styled(Flex)`
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
`;

const PageHeadRow = styled(Flex)`
  align-items: flex-start;
  justify-content: space-between;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    flex-direction: column;
    gap: 16px;
  }
`;

const FilterRow = styled(Flex)`
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  padding-bottom: 8px;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const FilterLeft = styled(Flex)`
  align-items: center;
  min-width: 0;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const PageTitle = styled(Text)`
  font-size: 42px;
  font-weight: 800;
  line-height: 1.1;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    font-size: 28px;
  }
`;

const Accent = styled.span`
  color: ${({ theme }) => theme.colors.envy};
`;

const PageSub = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 16px;
  margin-top: 8px;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    font-size: 14px;
  }
`;

const Tabs = styled(Flex)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.navy50};
  gap: 0;
`;

const Tab = styled.button<{ $active: boolean }>`
  align-items: center;
  background: none;
  border: none;
  border-bottom: 2px solid
    ${({ $active, theme }) => ($active ? theme.colors.envy : "transparent")};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.ice : theme.colors.chalk};
  cursor: pointer;
  display: flex;
  font-family: Barlow, sans-serif;
  font-size: 15px;
  font-weight: 600;
  gap: 8px;
  padding: 14px 24px;
  transition: all 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    font-size: 14px;
    gap: 6px;
    padding: 12px 16px;
  }
`;

const NewBadge = styled.span`
  background: #e53e3e;
  border-radius: 4px;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.5px;
  line-height: 1;
  padding: 3px 6px;
`;

const TabCount = styled.span`
  background: ${({ theme }) => theme.colors.navy50};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
`;

const Page = styled(Flex)`
  flex-direction: column;
  gap: 24px;
`;

const MyTeamsGrid = styled(Box)`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    grid-template-columns: 1fr;
  }
`;

const CreateCard = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy300};
  border: 2px dashed ${({ theme }) => theme.colors.navy50};
  border-radius: 10px;
  cursor: pointer;
  justify-content: center;
  min-height: 296px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

const CreateCardInner = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 10px;
  padding: 32px;
`;

const CreateCircle = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy50};
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.envy};
  height: 48px;
  justify-content: center;
  margin-bottom: 4px;
  width: 48px;
`;

const SortTabs = styled(Flex)`
  background: ${({ theme }) => theme.colors.navy300};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 8px;
  flex-shrink: 0;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    align-self: flex-start;
  }
`;

const SortTab = styled.button<{ $active: boolean }>`
  background: ${({ $active, theme }) =>
    $active ? theme.colors.navy50 : "transparent"};
  border: none;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.ice : theme.colors.chalk};
  cursor: pointer;
  font-family: Barlow, sans-serif;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 16px;
  transition: all 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const DiscoverGrid = styled(Box)`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    grid-template-columns: 1fr;
  }
`;

const EmptyState = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy300};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 10px;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  min-height: 300px;
  padding: 48px;
`;

const EmptyTitle = styled(Text)`
  font-size: 24px;
  font-weight: 700;
`;

const EmptyDesc = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 16px;
`;

const EmptyDiscover = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy300};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  justify-content: center;
  padding: 48px;
`;

const CreateLink = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  font-weight: 700;
  margin-left: 4px;
`;

const CreateButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 600;
  gap: 4px;
  height: 34px;
  padding: 0 14px;
  transition: opacity 0.15s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    height: 32px;
    padding: 0 10px;
    border-radius: 50%;
    width: 32px;
    justify-content: center;
  }
`;

const CreateButtonLabel = styled.span`
  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    display: none;
  }
`;

const LoadingContainer = styled(Flex)`
  align-items: center;
  justify-content: center;
  min-height: 300px;
  width: 100%;
`;

export default TeamsHub;
